<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" />

      <p>{{ $t('driverScore.text.driverScoreSummary') }}</p>

      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end d-flex flex-row">
          <b-dropdown id="unduh-dropdown" right variant="primary" menu-class="dropdown-menu-end">
            <template v-slot:button-content>
              <i class="mc-fa icon-outline_download"></i>{{ $t('global.action.download') }}
            </template>
            <b-dropdown-item href="#">
              <b-button class="mc-button-download" @click="downloadReport('excel')">
                <img :src="excelIcon" alt="excel" />
                {{ $t('global.action.downloadExcel') }}
              </b-button>
            </b-dropdown-item>
            <b-dropdown-item href="#">
              <b-button class="mc-button-download" @click="downloadReport('pdf')">
                <img :src="pdfIcon" alt="pdf" />
                {{ $t('global.action.downloadPdf') }}
              </b-button>
            </b-dropdown-item>
          </b-dropdown>

          <McButton
            v-if="hasAccessAll(['b4a-penilaian-pengemudi-v2:read'])"
            variant="secondary"
            @click="$router.push({ name: 'List Configuration V2' })"
            :text="''"
            icon="icon-outline_settings"
            class="ms-2"
          />
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-md-12 d-flex flex-row justify-content-end">
          <div class="mc-table-filter me-2">
            <date-picker
              type="month"
              v-model="periodMonth"
              format="MMM YYYY"
              value-type="YYYY-MM-DD"
              @input="changePeriod"
              :disabled-date="disabledRange"
              :placeholder="$t('global.action.selectMonth')"
              class="w-25"
              :clearable="false"
            ></date-picker>
          </div>
          <ModalFilter
            :activeFilters="activeFilters"
            @handleChangeActiveFilters="handleChangeActiveFilters"
          />
        </div>
      </div>
    </div>

    <div class="row" v-if="this.dataFields && this.load">
      <div class="mc-table col-12">
        <div class="card">
          <div class="card-body">
            <div class="row">
              <!-- Search -->
              <div class="col-sm-12 col-md-12 d-flex flex-row-reverse">
                <div class="mc-table-view">
                  <div id="tickets-table_filter" class="dataTables_filter text-md-end">
                    <label
                      class="d-inline-flex align-items-center mc-table-view__search"
                      @click="focusSearch"
                    >
                      <i class="mc-fa fas fa-search"></i>
                      <b-form-input
                        v-model="search"
                        type="search"
                        :placeholder="$t('global.search') + '...'"
                        :disabled="isBusy"
                        class="form-control ms-2"
                        @input="searchData"
                        ref="searchInput"
                      ></b-form-input>
                    </label>
                  </div>
                </div>
                <McFilterSelect
                  :defaultColumnOrder="initialFields"
                  :currentColumnOrder="dataFields"
                  @setColumn="handleSetDataFields"
                />
              </div>
              <!-- End search -->
            </div>

            <!-- Table -->
            <div class="table-responsive my-3 border border-1">
              <b-table-simple>
                <b-thead>
                  <b-tr>
                    <b-th
                      v-for="(field, index) in headerFields"
                      :key="field.key"
                      :rowspan="field.rowspan || 1"
                      :colspan="field.colspan || 1"
                      :class="
                        field.isSticky ? `mc-header-driver mc-header-driver--${index + 1}` : ''
                      "
                    >
                      <div @click="handleSortChange(field)">
                        {{ field.label }}
                        <div v-if="field.isSortable" class="d-flex align-items-center float-end">
                          <div v-if="showSortIcon(field, 'asc')" class="sort-icon-up">
                            <i class="mc-icon icon-outline_chevron-up"></i>
                          </div>
                          <div v-if="showSortIcon(field, 'desc')" class="sort-icon-down">
                            <i class="mc-icon icon-outline_chevron-down"></i>
                          </div>
                        </div>
                      </div>
                    </b-th>
                  </b-tr>
                  <b-tr>
                    <b-th
                      v-for="field in subHeaderFields"
                      :key="field.key"
                      :rowspan="field.rowspan || 1"
                      :colspan="field.colspan || 1"
                    >
                      <div>{{ field.label }}</div>
                    </b-th>
                  </b-tr>
                </b-thead>

                <b-tbody>
                  <b-tr v-if="isBusy || items.length == 0">
                    <b-td :colspan="bodyFields.length">
                      <div class="__busy" v-if="isBusy">
                        <b-spinner class="align-middle mb-2"></b-spinner>
                        <strong>{{ $t('global.text.loading') }}...</strong>
                      </div>
                      <div class="text-center my-4" v-if="items.length == 0 && !isBusy">
                        {{ $t('global.emptyTable') }}
                      </div>
                    </b-td>
                  </b-tr>

                  <template v-else>
                    <b-tr v-for="item in items" :key="item.id">
                      <b-td
                        v-for="(field, index) in bodyFields"
                        :class="
                          field.stickyColumn
                            ? `mc-header-driver mc-header-driver--${index + 1}`
                            : ''
                        "
                      >
                        <div
                          v-if="field.key == 'driver.fullname'"
                          class="text-primary clickable-field hover-pointer"
                          @click="onClicked(item)"
                        >
                          {{ item.driver.fullname }}
                        </div>

                        <div v-if="field.key == 'finalGrade'" class="d-flex justify-content-center">
                          <span class="badge rounded-pill m-0" :class="gradeClass[item.finalGrade]">
                            {{ item.finalGrade == 'N' ? '-' : item.finalGrade }}
                          </span>
                        </div>

                        <div v-if="field.key === 'score' || field.key === 'totalDriven'">
                          {{ item[field.key] ?? '-' }}
                        </div>

                        <div v-if="field.key === 'driverStatus'" class="text-nowrap">
                          <span
                            :class="`mc-status mc-status--${
                              item.driver.isActive ? 'green' : 'grey'
                            }`"
                          >
                            {{
                              item.driver.isActive ? $t('global.active') : $t('global.nonActive')
                            }}
                          </span>
                        </div>
                        <template v-if="hasScopeFilterVGroupsDriverScore">
                          <div v-if="field.key === 'vehicleGroups'">
                            {{
                              item.vehicleGroups.length
                                ? item.vehicleGroups?.map((group) => group.name).join(', ') ?? '-'
                                : '-'
                            }}
                          </div>
                        </template>
                        <div v-if="field.name">
                          {{
                            field.name == 'score'
                              ? parseFloat(item[field.parent][field.name]).toFixed(2)
                              : item[field.parent][field.name] ?? '-'
                          }}
                        </div>
                      </b-td>
                    </b-tr>
                  </template>
                </b-tbody>
              </b-table-simple>
            </div>
            <div class="row">
              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_length" class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    {{ $t('global.show') }}
                    <b-form-select
                      class="form-select form-select-sm"
                      size="sm"
                      :disabled="isBusy"
                      :options="limitOptions"
                      :value="limit"
                      @input="handleLimitChange"
                    >
                    </b-form-select>
                    {{ $t('global.object.data').toLowerCase() }}.
                    <p>
                      <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
                      {{ $t('global.object.of') }} <strong>{{ totalCount }}</strong
                      >.
                    </p>
                  </label>
                </div>
              </div>
              <div class="col-sm-12 col-md-6">
                <div class="dataTables_paginate paging_simple_numbers float-end">
                  <ul class="pagination pagination-rounded mb-0">
                    <!-- pagination -->
                    <b-pagination
                      :disabled="isBusy"
                      :per-page="limit"
                      :total-rows="totalCount"
                      :value="page"
                      @change="handlePageChange"
                    ></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import { Helper, debounce } from '@/mixin/helper'
import responsive from '@/mixin/responsive'
import { MixPanel } from '@/mixin/mixpanel'
import { mapGetters } from 'vuex'
import moment from 'moment'
import { GET_DRIVER_SCORES } from '@/services/api/driverscore/score'
import McButton from '@/components/McButton'
import McFilterSelect from '@/components/McFilterSelect'
import ModalFilter from './ModalFilter'
import cloneDeep from 'lodash/cloneDeep'
import flattenDeep from 'lodash/flattenDeep'
import { DRIVER_SCORE_REPORT } from '@/services/api/report/report'

moment.locale('id')

const cloneFields = (fields) =>
  cloneDeep(fields).map((field) => {
    if (field.fields) {
      const subFields = field.fields.map((item) => ({ ...item, shown: true }))
      field.fields = subFields
    }

    return { ...field, shown: field.shown ?? true }
  })

/**
 * Starter component
 */
export default {
  page: {
    title: 'Driver Score',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    ModalFilter,
    McFilterSelect,
  },
  mixins: [Helper, MixPanel, responsive],

  data() {
    const dataParam = this.$store.state.identity.featureFlag.find(
      (feature) => feature.key == 'vsms2_driverscore'
    )
    return {
      excelIcon: require('@/assets/images/download-format/excel.png'),
      pdfIcon: require('@/assets/images/download-format/pdf.png'),
      periodMonth: this.now(),
      page: 1,
      limit: 10,
      totalCount: 0,
      limitOptions: [10, 25, 50, 100],
      isBusy: true,
      search: '',
      sorts: [],
      sortsValue: '',
      items: [],
      ids: [],
      dateParam: JSON.parse(dataParam.value),
      pastDate: this.momentAdd(new Date(), -3, 'M'),
      futureEnd: new Date(),
      gradeClass: {
        A: 'bg-success',
        B: 'bg-success',
        C: 'bg-warning',
        D: 'bg-danger',
        E: 'bg-danger',
        N: 'bg-secondary',
        '-': 'bg-secondary',
      },
      activeFilters: {
        drivers: [],
        finalGrade: {},
        vgroups: [],
      },
      dataFields: this.fields ? cloneFields(this.fields) : null,
      finalGrade: [],
      load: true,
      showFields: JSON.parse(localStorage.getItem('driverScoreFields')),
      driverId: [],
      vgroupId: [],
    }
  },
  computed: {
    locale() {
      return this.$i18n.locale
    },

    title() {
      return this.$t('driverScore.title.driverScore')
    },
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagData: 'identity/getFeatureFlagData',
    }),
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    isDms() {
      return this.hasAccessAll(['vehicle_video_trackers:read'])
    },
    hasScopeFilterVGroupsDriverScore() {
      return this.hasAccessAll(['b4b-filter-grup-kendaraan-penilaian-pengemudi:read'])
    },

    initialFields() {
      return cloneFields(this.fields)
    },

    fields() {
      let field = [
        {
          key: 'driver.fullname',
          header: 'driver',
          label: this.$t('global.object.driverName'),
          rowspan: 2,
          isLocked: true,
          isSticky: true,
          isSortable: true,
          stickyColumn: true,
        },
        {
          key: 'score',
          header: 'score',
          label: this.$t('driverScore.field.totalScore'),
          rowspan: 2,
          formatter: (value) => {
            return value == '-' ? value : this.localeThousandFormat(this.minimizeFloat(value))
          },
          isLocked: true,
          isSticky: true,
          isSortable: true,
          stickyColumn: true,
        },
        {
          key: 'finalGrade',
          header: 'index',
          label: this.$t('global.object.index'),
          rowspan: 2,
          isLocked: true,
          isSticky: true,
          isSortable: true,
          stickyColumn: true,
        },
        {
          key: 'totalDriven',
          header: 'totalDriven',
          label: this.$t('driverScore.field.totalTrip'),
          rowspan: 2,
          isLocked: true,
          isSticky: true,
          isSortable: true,
          stickyColumn: true,
        },
        ...(this.hasScopeFilterVGroupsDriverScore
          ? [
              {
                key: 'vehicleGroups',
                header: 'vehicleGroups',
                label: this.$t('report.fields.vehicleGroup'),
                rowspan: 2,
                isLocked: true,
                isSticky: true,
                isSortable: true,
                stickyColumn: true,
              },
            ]
          : []),

        {
          key: 'harshAccel',
          header: 'harshAccels',
          label: this.$t('global.alert.suddenAcceleration'),
          rowspan: 1,
          colspan: 2,
          fields: [
            {
              key: 'harshAccel.total',
              parent: 'harshAccel',
              name: 'total',
              label: this.$t('driverScore.field.occurences'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
            {
              key: 'harshAccel.score',
              parent: 'harshAccel',
              name: 'score',
              label: this.$t('driverScore.field.score'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
          ],
        },
        {
          key: 'harshBrake',
          header: 'harshBrakes',
          label: this.$t('global.alert.suddenDeceleration'),
          rowspan: 1,
          colspan: 2,
          fields: [
            {
              key: 'harshBrake.total',
              parent: 'harshBrake',
              name: 'total',
              label: this.$t('driverScore.field.occurences'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
            {
              key: 'harshBrake.score',
              parent: 'harshBrake',
              name: 'score',
              label: this.$t('driverScore.field.score'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
          ],
        },
        {
          key: 'sharpturn',
          header: 'sharpTurns',
          label: this.$t('global.alert.sharpTurn'),
          rowspan: 1,
          colspan: 2,
          fields: [
            {
              key: 'sharpturn.total',
              parent: 'sharpturn',
              name: 'total',
              label: this.$t('driverScore.field.occurences'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
            {
              key: 'sharpturn.score',
              parent: 'sharpturn',
              name: 'score',
              label: this.$t('driverScore.field.score'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
          ],
        },
        {
          key: 'overspeed',
          header: 'overspeed',
          label: this.$t('global.alert.overspeed'),
          rowspan: 1,
          colspan: 2,
          fields: [
            {
              key: 'overspeed.total',
              parent: 'overspeed',
              name: 'total',
              label: this.$t('driverScore.field.occurences'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
            {
              key: 'overspeed.score',
              parent: 'overspeed',
              name: 'score',
              label: this.$t('driverScore.field.score'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
          ],
        },
        {
          key: 'exceededIdleTime',
          header: 'idleExceeded',
          label: this.$t('global.alert.idleTime'),
          rowspan: 1,
          colspan: 2,
          fields: [
            {
              key: 'exceededIdleTime.total',
              parent: 'exceededIdleTime',
              name: 'total',
              label: this.$t('driverScore.field.occurences'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
            {
              key: 'exceededIdleTime.score',
              parent: 'exceededIdleTime',
              name: 'score',
              label: this.$t('driverScore.field.score'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
          ],
        },
        {
          key: 'exceededParkingTime',
          header: 'parkExceeded',
          label: this.$t('global.alert.parkTime'),
          rowspan: 1,
          colspan: 2,
          fields: [
            {
              key: 'exceededParkingTime.total',
              parent: 'exceededParkingTime',
              name: 'total',
              label: this.$t('driverScore.field.occurences'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
            {
              key: 'exceededParkingTime.score',
              parent: 'exceededParkingTime',
              name: 'score',
              label: this.$t('driverScore.field.score'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
          ],
        },
      ]
      const dmsField = [
        {
          key: 'smoking',
          header: 'smoking',
          label: this.$t('global.alert.smoking'),
          rowspan: 1,
          colspan: 2,
          fields: [
            {
              key: 'smoking.total',
              parent: 'smoking',
              name: 'total',
              label: this.$t('driverScore.field.occurences'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
            {
              key: 'smoking.score',
              parent: 'smoking',
              name: 'score',
              label: this.$t('driverScore.field.score'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
          ],
        },
        {
          key: 'yawn',
          header: 'yawn',
          label: this.$t('global.alert.yawning'),
          rowspan: 1,
          colspan: 2,
          fields: [
            {
              key: 'yawn.total',
              parent: 'yawn',
              name: 'total',
              label: this.$t('driverScore.field.occurences'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
            {
              key: 'yawn.score',
              parent: 'yawn',
              name: 'score',
              label: this.$t('driverScore.field.score'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
          ],
        },
        {
          key: 'eyeClosed',
          header: 'eyeClosed',
          label: this.$t('global.alert.asleep'),
          rowspan: 1,
          colspan: 2,
          fields: [
            {
              key: 'eyeClosed.total',
              parent: 'eyeClosed',
              name: 'total',
              label: this.$t('driverScore.field.occurences'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
            {
              key: 'eyeClosed.score',
              parent: 'eyeClosed',
              name: 'score',
              label: this.$t('driverScore.field.score'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
          ],
        },
        {
          key: 'phoneCalling',
          header: 'phoneCalling',
          label: this.$t('global.alert.calling'),
          rowspan: 1,
          colspan: 2,
          fields: [
            {
              key: 'phoneCalling.total',
              parent: 'phoneCalling',
              name: 'total',
              label: this.$t('driverScore.field.occurences'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
            {
              key: 'phoneCalling.score',
              parent: 'phoneCalling',
              name: 'score',
              label: this.$t('driverScore.field.score'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
          ],
        },
        {
          key: 'distraction',
          header: 'distraction',
          label: this.$t('global.alert.disturbance'),
          rowspan: 1,
          colspan: 2,
          fields: [
            {
              key: 'distraction.total',
              parent: 'distraction',
              name: 'total',
              label: this.$t('driverScore.field.occurences'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
            {
              key: 'distraction.score',
              parent: 'distraction',
              name: 'score',
              label: this.$t('driverScore.field.score'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
          ],
        },
        {
          key: 'missingFace',
          header: 'missingFace',
          label: this.$t('global.alert.outOfFrame'),
          rowspan: 1,
          colspan: 2,
          fields: [
            {
              key: 'missingFace.total',
              parent: 'missingFace',
              name: 'total',
              label: this.$t('driverScore.field.occurences'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
            {
              key: 'missingFace.score',
              parent: 'missingFace',
              name: 'score',
              label: this.$t('driverScore.field.score'),
              colspan: 1,
              isLocked: true,
              isSticky: true,
            },
          ],
        },
      ]
      if (this.isDms) field.push(...dmsField)

      field.push({
        key: 'driverStatus',
        header: 'driverStatus',
        label: this.$t('master.form.driver.driverStatus.text'),
        rowspan: 2,
        shown: false,
      })

      return field
    },

    headerFields() {
      const fields = []

      this.dataFields?.forEach((item) => {
        if (!item.shown) return

        let obj = Object.assign({}, item)

        const childShown = []
        if (item.fields) {
          item.fields.forEach((child) => {
            if (child.shown == false) return

            childShown.push(child)
          })

          if (childShown.length == 1) {
            obj = Object.assign({}, childShown[0])

            obj.rowspan = 2
          }
          delete obj.fields
        }

        fields.push(obj)
      })

      return fields
    },

    subHeaderFields() {
      const fields = []

      this.dataFields?.forEach((item) => {
        if (!item.shown || !item.fields) return

        const childShown = []
        item.fields.forEach((el) => {
          if (!el.shown) return
          childShown.push(el)
        })

        if (childShown.length === 1) return

        fields.push(childShown)
      })

      return flattenDeep(fields)
    },

    bodyFields() {
      const fields = []

      this.dataFields?.forEach((item) => {
        if (!item.shown) return

        if (item.fields) {
          const newFields = item.fields.filter((el) => el.shown)
          fields.push(cloneDeep(newFields))
        } else {
          if (!item.shown) return

          const obj = Object.assign({}, item)
          fields.push(obj)
        }
      })

      return flattenDeep(fields)
    },

    columns() {
      let filteredFields = []
      this.dataFields.map((field) => {
        if (field.shown) filteredFields.push(field.header)
      })

      return filteredFields
    },
  },

  watch: {
    async locale() {
      this.load = false
      this.dataFields = cloneFields(this.fields)
      this.load = true
    },
  },

  async mounted() {
    if (!this.mediaQuery.matches) {
      this.$store.dispatch('layout/toggleMenu', true)
    }
    this.dataFields = cloneFields(this.fields)

    this.track('View Driver Score Page')
    await this.$nextTick()
    await this.getData({
      limit: this.limit,
      page: this.page,
      filters: {
        month: this.momentM(new Date(this.periodMonth)),
        year: this.momentY(new Date(this.periodMonth)),
      },
    })
  },

  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    disabledRange: function (date) {
      return date <= new Date(this.dateParam.active_date) || date >= new Date()
    },
    replaceNullWithDash(obj) {
      for (const key in obj) {
        if (obj[key] === null) {
          obj[key] = '-'
        } else if (typeof obj[key] === 'object') {
          this.replaceNullWithDash(obj[key]) // Recursive call for nested objects
        }
      }
    },
    buildDriverScoreFilter(opts = {}) {
      const driverId =
        this.activeFilters?.drivers?.length > 0
          ? this.activeFilters.drivers.map((item) => Number(item))
          : null

      // Empty vgroup selection must stay undefined. Backend owns branch-scope expansion.
      const vgroupId =
        this.activeFilters?.vgroups?.length > 0
          ? this.activeFilters.vgroups.map((item) => Number(item))
          : undefined

      this.driverId = driverId
      this.vgroupId = vgroupId

      const finalGrade = Object.keys(this.activeFilters.finalGrade)

      const index =
        this.activeFilters.finalGrade.all == true
          ? []
          : finalGrade.filter((item) => this.activeFilters.finalGrade[item])
      this.finalGrade = index

      const driverStatus = this.activeFilters.driverStatus
        ? this.checkStatus(this.activeFilters.driverStatus)
        : {
            activeUser: true,
          }

      return {
        month: opts.month ?? this.momentM(new Date(this.periodMonth)),
        year: opts.year ?? this.momentY(new Date(this.periodMonth)),
        search: opts.search,
        orderBy: opts.sorts,
        driverId,
        finalGrade: index,
        vgroupId,
        ...driverStatus,
      }
    },
    async getData(opts) {
      this.isBusy = true

      const filter = this.buildDriverScoreFilter({
        month: opts.filters.month,
        year: opts.filters.year,
        search: opts.search,
        sorts: opts.sorts,
      })

      const res = await this.$apollo.query({
        query: GET_DRIVER_SCORES,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            ...filter,
          },
        },
      })

      if (!res) return

      const data = res.data.driverScores.driverScores
      this.ids = data.data.map((item) => item.driverId)

      this.page = data.pagination.page
      this.totalCount = data.pagination.total
      this.items = data.data.map((item) => {
        const clonedItem = { ...item } // Create a shallow copy of the item
        this.replaceNullWithDash(clonedItem) // Replace null values with '-'
        return clonedItem
      })
      this.items.forEach((item) => {
        if (item.index === '-') {
          item.index = 'N'
        }
      })

      this.isBusy = false
    },
    searchData: debounce(async function (_search) {
      this.search = _search
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts: this.sortsValue != '' ? this.sortsValue : undefined,
        filters: {
          month: this.momentM(new Date(this.periodMonth)),
          year: this.momentY(new Date(this.periodMonth)),
        },
      }
      await this.getData(opts)
      this.$refs['search-input-ref'].$el.focus()
    }, 500),
    async changePeriod() {
      this.page = 1
      this.limit = 10

      await this.getData({
        limit: this.limit,
        page: this.page,
        filters: {
          month: this.momentM(new Date(this.periodMonth)),
          year: this.momentY(new Date(this.periodMonth)),
        },
      })
    },
    async handleLimitChange(_limit) {
      this.limit = _limit

      await this.getData({
        limit: _limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
        sorts: this.sortsValue != '' ? this.sortsValue : undefined,
        filters: {
          month: this.momentM(new Date(this.periodMonth)),
          year: this.momentY(new Date(this.periodMonth)),
        },
      })
    },
    async handlePageChange(newPage) {
      const opts = {
        page: newPage,
        search: this.search != '' ? this.search : undefined,
        limit: this.limit,
        sorts: this.sortsValue != '' ? this.sortsValue : undefined,
        filters: {
          month: this.momentM(new Date(this.periodMonth)),
          year: this.momentY(new Date(this.periodMonth)),
        },
      }
      await this.getData(opts)
    },
    showSortIcon(field, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === field.key && currentColumnSort.order === order
    },
    async handleSortChange(field) {
      if (!field) {
        this.sorts = []
        return
      }

      if (!field?.isSortable) return
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === field.key)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]
      await this.$nextTick()
      await this.getData({
        limit: this.limit,
        page: this.page,
        filters: {
          month: this.momentM(new Date(this.periodMonth)),
          year: this.momentY(new Date(this.periodMonth)),
        },
        sorts:
          this.sorts.length == 0
            ? undefined
            : this.sorts.map((sort) => {
                const obj = {}
                obj[sort.key] = sort.order
                return JSON.stringify(obj)
              }),
      })
    },
    onClicked(item) {
      const date = {
        month: this.momentM(new Date(this.periodMonth)),
        year: this.momentY(new Date(this.periodMonth)),
      }
      localStorage.setItem('driverscore-filter-date', JSON.stringify(date))

      if (!item.totalDriven) {
        this.$mcToast.info({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                tag: 'h6',
                text: this.$t('driverScore.swalText.noScore'),
              },
              { tag: 'span', text: this.$t('global.driver') },
              {
                tag: 'strong',
                text: item.driver.fullname,
                class: '__blue-highlight-text',
              },
              { tag: 'span', text: this.$t('driverScore.swalText.noScoreInfo') },
            ],
          },
        })
      } else {
        this.$router.push({
          name: 'Detail Driver Score V2',
          params: { driverId: item.driverId },
        })
      }
    },

    handleChangeActiveFilters(filters) {
      filters.forEach((filter) => {
        // to reset the filter values
        this.$delete(this.activeFilters, filter.key)
        this.$set(this.activeFilters, filter.key, filter.values)
      })

      this.filter = filters
      this.refreshData()
    },

    async refreshData() {
      await this.getData({
        limit: this.limit,
        page: 1,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sortsValue != '' ? this.sortsValue : undefined,
        filters: {
          month: this.momentM(new Date(this.periodMonth)),
          year: this.momentY(new Date(this.periodMonth)),
        },
      })
    },

    handleSetDataFields(fields) {
      this.dataFields = cloneDeep(fields)
    },

    downloadReport(extension) {
      let filterVar = {
        ...this.buildDriverScoreFilter(),
        column: this.columns,
        subtitle: `Periode: ${this.momentYM(new Date(this.periodMonth))}`,
        downloadAs: extension == 'pdf' ? 'pdf' : 'excel',
      }

      const fileType = extension == 'pdf' ? 'pdf' : 'vnd.ms-excel'
      const fileExt = extension == 'pdf' ? 'pdf' : 'xlsx'

      this.$apollo
        .query({
          query: DRIVER_SCORE_REPORT,
          variables: {
            filter: filterVar,
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          if (res.data.driverScoresReport.isSuccessful) {
            let a = document.createElement('a') //Create <a>
            a.href =
              `data:application/${fileType};base64,` +
              res.data.driverScoresReport.driverScoresReport.base64 //Image Base64 Goes here
            a.download = `${this.$t('driverScore.title.driverScore')} ${this.momentMMM(
              this.momentM(new Date(this.periodMonth))
            )} ${this.momentYmDHms(new Date())}.${fileExt}` //File name Here
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
          } else {
            self.$mcToast.error(this.$t('global.alert.text.internalServerError'))
          }
        })
    },

    checkStatus(filter) {
      if ((filter.active && filter.inactive) || (!filter.active && !filter.inactive)) {
        return {}
      } else if (filter.active) {
        return { activeUser: true }
      } else if (filter.inactive) {
        return { activeUser: false }
      }
    },
  },
}
</script>
<style lang="scss" scoped>
@import './style.scss';

:deep(.mc-button-download) {
  background-color: white;
  width: 100%;
  border: 1px solid $mep-primary;
  color: $mep-primary;
  font-weight: 600;
  padding: 5px 20px;
  min-width: 200px;
  white-space: nowrap;

  span {
    margin-right: 2px;
  }

  &:not(:last-child) {
    margin-bottom: 8px;
  }

  &:hover {
    background-color: $mep-primary;
    border-color: $mep-primary;
    border: 1px solid white;
    color: white;
  }
}

.hover-pointer {
  &:hover {
    cursor: pointer;
  }
}
</style>
